import { cn } from '@latitude/token-utils';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { type VariantProps } from 'class-variance-authority';
import React, { useMemo } from 'react';
import type { Ref } from 'react';
import type { DrawerProps } from 'types';
import { sheetVariants } from 'variants/sheetVariants';

import { SheetOverlay } from '../SheetOverlay';

export const SheetContent = ({
  side = 'right',
  className,
  children,
  hideOverlay,
  bluredOverlay,
  portalContent,
  fixed = true,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> &
  VariantProps<typeof sheetVariants> &
  Pick<DrawerProps, 'hideOverlay' | 'portalContent' | 'bluredOverlay'> & {
    ref?: Ref<React.ComponentRef<typeof SheetPrimitive.Content>>;
  }) => {
  const content = useMemo(
    () => (
      <>
        {!hideOverlay ? <SheetOverlay bluredOverlay={bluredOverlay} /> : null}
        <SheetPrimitive.Content
          ref={ref}
          data-testid="drawer-content"
          data-with-portal={Boolean(portalContent).toString()}
          className={cn(sheetVariants({ side, fixed }), className)}
          {...props}
        >
          {children}
        </SheetPrimitive.Content>
      </>
    ),
    [
      bluredOverlay,
      children,
      className,
      fixed,
      hideOverlay,
      portalContent,
      props,
      ref,
      side,
    ],
  );

  return portalContent ? (
    <SheetPrimitive.Portal data-testid="drawer-portal">
      {content}
    </SheetPrimitive.Portal>
  ) : (
    content
  );
};
SheetContent.displayName = SheetPrimitive.Content.displayName;
